import React, { memo, useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
import Content from 'studio/components/Content/Content';
import styles from './Integrations.styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import dbtLogo from 'studio/images/dbt-logo.svg?raw';
import slackLogo from 'studio/images/slack-logo.svg?raw';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from 'studio/components/TextField';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import services from 'services';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import PageTitle from 'studio/components/PageTitle';
import AppsIcon from '@mui/icons-material/AppsRounded';
import { trackEvent } from 'utils/eventTracking';
import Link from 'studio/components/Link';

export const integrationTypes = {
  slack: 'slack',
  dbtCloud: 'dbt-cloud'
};

const Integrations = ({ classes }) => {
  useEffect(() => {
    trackEvent('Admin APIs and Integrations Opened');
  }, []);

  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();

  // the value of the dbt service token field in the dialog
  const [dbtServiceToken, setDbtServiceToken] = useState(null);
  // specifies whether to show the dialog to configure the dbt cloud integration
  const [showDbtCloudDialog, setShowDbtCloudDialog] = useState(false);

  // the value of the Slack Webhook field in the dialog
  const [slackWebhookUrl, setSlackWebhookUrl] = useState(null);
  // specifies whether to show the dialog to configure the Slack integration
  const [showSlackDialog, setShowSlackDialog] = useState(false);

  // On mount get the already existing configurations
  useEffect(() => {
    async function getIntegrations() {
      const result = await withLoader(services.getIntegrations());
      if (result.success) {
        result.data.forEach(int => {
          const { type, config } = int;
          if (type === integrationTypes.slack) {
            setSlackWebhookUrl(config.webhookUrl);
          } else if (type === integrationTypes.dbtCloud) {
            setDbtServiceToken(config.serviceToken);
          }
        });
      }
    }
    getIntegrations();
  }, [withLoader]);

  const updateDbtCloudServiceToken = useCallback(
    async dbtServiceToken => {
      const body = { serviceToken: dbtServiceToken };
      const result = await withLoader(services.updateIntegration(integrationTypes.dbtCloud, body));
      if (result.success) {
        const message = `The dbt Service token has been successfully verified and configured. You are now able to sync information to Veezoo from your dbt Jobs in the Knowledge Graph section.`;
        successMessage(message);
        // Let's close the dialogue if the token has been successfully configured
        setShowDbtCloudDialog(false);
        return;
      }
      errorMessage(result?.error?.message || 'There was an error verifying the service token.');
    },
    [dbtServiceToken, withLoader, successMessage, errorMessage]
  );

  const updateSlackWebhookUrl = useCallback(
    async slackWebhookUrl => {
      const body = { webhookUrl: slackWebhookUrl };
      const result = await withLoader(services.updateIntegration(integrationTypes.slack, body));
      if (result.success) {
        const message = `The Slack Webhook was successfully configured.`;
        successMessage(message);
        // Let's close the dialogue if the token has been successfully configured
        setShowSlackDialog(false);
        return;
      }
      errorMessage(result?.error?.message || 'There was an error setting the webhook URL.');
    },
    [slackWebhookUrl, withLoader, successMessage, errorMessage]
  );

  return (
    <Content>
      <PageTitle icon={AppsIcon}>APIs & Integrations</PageTitle>
      <Grid container spacing={3}>
        <Grid item sm={6} md={4} xl={3}>
          <Card>
            <div>
              <CardMedia component="img" height={150} image={dbtLogo} className={classes.cardMedia} alt="dbt Cloud" />
            </div>
            <CardContent>
              <Typography gutterBottom variant="h6" component="div">
                dbt Cloud
              </Typography>
              <Typography variant="body2">
                Leverage your dbt configuration to automatically enrich the Knowledge Graph with descriptions.
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" color="primary" onClick={() => setShowDbtCloudDialog(true)}>
                Configure
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item sm={6} md={4} xl={3}>
          <Card>
            <CardMedia component="img" image={slackLogo} className={classes.cardMedia} height={150} alt="slack" />
            <CardContent>
              <Typography gutterBottom variant="h6" component="div">
                Slack
              </Typography>
              <Typography variant="body2">
                Get a live feed over all questions being asked to Veezoo directly to your Slack channel.
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" color="primary" onClick={() => setShowSlackDialog(true)}>
                Configure
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
      <Dialog fullWidth maxWidth="sm" open={showDbtCloudDialog} onClose={() => setShowDbtCloudDialog(false)}>
        <DialogTitle>Configure dbt Cloud Integration</DialogTitle>
        <DialogContent>
          Enter the Service Token created in dbt Cloud.
          <br />
          <br />
          <TextField
            value={dbtServiceToken}
            label="Service Token"
            description="The Service Token needs only Metadata permissions"
            onChange={event => setDbtServiceToken(event.target.value)}
            onPaste={event => {
              event.preventDefault();
              const input = event.clipboardData.getData('Text');
              setDbtServiceToken(input);
            }}
            autoFocus
            data-test="dbtServiceTokenTextfield"
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setShowDbtCloudDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="primary"
            data-test="saveButton"
            disabled={loading}
            onClick={() => updateDbtCloudServiceToken(dbtServiceToken)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog fullWidth maxWidth="sm" open={showSlackDialog} onClose={() => setShowSlackDialog(false)}>
        <DialogTitle>Configure Slack Integration</DialogTitle>
        <DialogContent>
          Setting up the Veezoo Slack integration is straight forward.
          <br />
          <br />
          Follow{' '}
          <Link href="https://api.slack.com/messaging/webhooks#getting_started" newWindow>
            the official Slack guide
          </Link>{' '}
          to create an app and paste the Incoming Webhook URL received in Step 3 in the field below.
          <br />
          <br />
          The Channel you choose when creating the App is where Veezoo will log the events to.
          <br />
          <br />
          <TextField
            value={slackWebhookUrl}
            label="Incoming Webhook URL"
            description="Copy from Your App > Settings > Incoming Webhooks > Webhook URL"
            onChange={event => setSlackWebhookUrl(event.target.value)}
            onPaste={event => {
              event.preventDefault();
              const input = event.clipboardData.getData('Text');
              setSlackWebhookUrl(input);
            }}
            autoFocus
            data-test="slackWebhookUrlTextfield"
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setShowSlackDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="primary"
            data-test="saveButton"
            disabled={loading}
            onClick={() => updateSlackWebhookUrl(slackWebhookUrl)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
};

export default memo(withStyles(styles)(Integrations));
